import { Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AmazonSalesResponse } from "reedy-data/api";
import { formatBucketDate, formatNumber } from "utils/formatting";
import { Text } from "components/text";
import { SummaryCard } from "./SummaryCard";

// Rank dates come from the bucket carrying each extreme, not the summary alone.
export const AmazonRankingCard = ({
  sales,
  isLoading,
  unavailableReason,
  onClick,
}: {
  sales?: AmazonSalesResponse;
  isLoading?: boolean;
  unavailableReason?: string;
  onClick: () => void;
}) => {
  const { t } = useTranslation(["productDetails"]);

  const summary = sales?.summary;
  const bestRankDate = sales?.buckets.find(
    (bucket) => bucket.salesRank === summary?.bestRank,
  )?.salesRankDate;
  const worstBucket = sales?.buckets.find(
    (bucket) => bucket.worstSalesRank === summary?.worstRank,
  );

  const title =
    sales && bestRankDate
      ? t("salesData.summaryCards.amazonRanking.titleWithDate", {
          date: formatBucketDate(bestRankDate),
        })
      : t("salesData.summaryCards.amazonRanking.title");

  const detail =
    !unavailableReason &&
    summary?.worstRank != null &&
    worstBucket?.worstSalesRankDate
      ? {
          summary: t("salesData.summaryCards.amazonRanking.worst", {
            date: formatBucketDate(worstBucket.worstSalesRankDate),
          }),
          value: `#${formatNumber(summary.worstRank)}`,
        }
      : undefined;

  return (
    <SummaryCard
      testId="summary-card-amazon-ranking"
      icon={<Trophy className="h-4 w-4" aria-hidden />}
      title={title}
      isLoading={isLoading}
      badge={null}
      body={
        sales ? (
          <Text
            as="span"
            variant="pageHeading"
            numeric="tabular"
            className="truncate"
          >
            {summary?.bestRank != null
              ? `#${formatNumber(summary.bestRank)}`
              : "—"}
          </Text>
        ) : undefined
      }
      series={
        sales
          ? [
              {
                key: "summary-card-amazon-ranking",
                color: "var(--chart-6)",
                // Lower rank is better, so plot the negated rank: a rising line
                // means an improving rank, matching the (inverted-axis) Amazon
                // sales chart. Gaps stay null so the line bridges them rather
                // than dropping to zero.
                points: sales.buckets.map((bucket) =>
                  bucket.salesRank == null ? null : -bucket.salesRank,
                ),
              },
            ]
          : undefined
      }
      unavailableReason={unavailableReason}
      detailSummary={detail?.summary}
      detailValue={detail?.value}
      jumpHint={t("salesData.summaryCards.jumpToAmazonChart")}
      onClick={onClick}
    />
  );
};
